import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { FitResult, FitVerdict } from '@pairing/core/capabilities/fit';
import { errorMessage } from '../../api/errors';
import { Dialog } from '../../components/ui';
import ui from '../../components/ui.module.css';
import { Field, Input, Textarea } from '../../components/fields';
import { fitCheck, NEXT_STEPS_BY_VERDICT, verdictLabel } from '../content/fitCheck';
import { LeadText } from '../lib/marks';
import { publicPost } from '../lib/publicApi';
import { BookMeeting } from './BookMeeting';
import s from '../site.module.css';

/**
 * The fit check box on /fit-check. It wraps the existing open endpoint (POST /v1/fit/check, ADR 0007 R1) unchanged:
 * same body ({ scenario }), same answer (FitResult). The verdict logic lives in @pairing/core and is not touched here.
 */

/** FIT_SCENARIO_MAX in @pairing/core/capabilities/serve; the API enforces it. */
export const SCENARIO_MAX = 4000;

/** How long to wait before saying the check didn't respond. The API looks systems up for up to 35 seconds first. */
export const FIT_TIMEOUT_MS = 45_000;

/** Fits and likely fits in leaf; needs review and partial in bloom; not a fit in neutral navy. Never stem: it isn't an error. */
export const VERDICT_TONE: Record<FitVerdict, 'fit' | 'review' | 'notFit'> = {
  fits: 'fit',
  likely_fits: 'fit',
  needs_review: 'review',
  partial: 'review',
  not_a_fit: 'notFit',
  off_topic: 'notFit',
};

const COUNT_KEY = 'pairing.fitChecks';
const TALK_SHOWN_KEY = 'pairing.fitTalkShown';

/**
 * Counts this browser session's answered checks (sessionStorage) and says whether to offer the talk-to-Denamico popup:
 * once, on the first check past the limit. Storage can be missing or blocked; then the popup never shows.
 */
export function countCheck(limit: number, storage: Storage | undefined = globalThis.sessionStorage): boolean {
  try {
    if (!storage) return false;
    const count = Number(storage.getItem(COUNT_KEY) ?? '0') + 1;
    storage.setItem(COUNT_KEY, String(count));
    if (count <= limit || storage.getItem(TALK_SHOWN_KEY)) return false;
    storage.setItem(TALK_SHOWN_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

/** Adds a quick-add chip's word to the end of the description. */
export function appendChip(text: string, chip: string): string {
  const trimmed = text.replace(/\s+$/, '');
  return trimmed ? `${trimmed} ${chip}` : chip;
}

export function FitCheck() {
  const h = fitCheck.hero;
  const r = fitCheck.result;
  const [scenario, setScenario] = useState('');
  const [asked, setAsked] = useState('');
  const [result, setResult] = useState<FitResult | null>(null);
  const [state, setState] = useState<'idle' | 'checking' | 'error'>('idle');
  const [empty, setEmpty] = useState(false);
  const [talk, setTalk] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (result) resultRef.current?.focus();
  }, [result]);

  async function check(e: FormEvent) {
    e.preventDefault();
    const text = scenario.trim();
    if (!text) {
      setEmpty(true);
      const field = (e.currentTarget as HTMLFormElement).elements.namedItem('scenario');
      if (field instanceof HTMLElement) field.focus();
      return;
    }
    setEmpty(false);
    setState('checking');
    try {
      const answer = await publicPost<FitResult>('/v1/fit/check', { scenario: text }, { timeoutMs: FIT_TIMEOUT_MS });
      setResult(answer);
      setAsked(text);
      setState('idle');
      if (countCheck(fitCheck.talk.afterChecks)) setTalk(true);
    } catch {
      setState('error');
    }
  }

  return (
    <div className={s.fitBox}>
      <form onSubmit={check} noValidate className={s.stack}>
        <Field label={h.inputLabel} hint={h.hint} error={empty ? r.empty : null}>
          <Textarea
            name="scenario"
            rows={6}
            maxLength={SCENARIO_MAX}
            placeholder={h.placeholder}
            value={scenario}
            onChange={(e) => setScenario(e.target.value)}
          />
        </Field>
        <div className={s.stack} style={{ gap: 'var(--space-2)' }}>
          <p className={s.small} id="fit-chips">
            {h.chipsLabel}
          </p>
          <ul className={s.chips} aria-labelledby="fit-chips">
            {h.chips.map((chip) => (
              <li key={chip}>
                <button type="button" className={s.chip} onClick={() => setScenario((t) => appendChip(t, chip))}>
                  {chip}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <button type="submit" className={`${ui.button} ${ui.primary}`} aria-busy={state === 'checking' || undefined} disabled={state === 'checking'}>
            {h.button}
          </button>
        </div>
      </form>

      <div aria-live="polite">
        {state === 'checking' && <p className={s.small}>{r.loading}</p>}
        {state === 'error' && (
          <p className={s.errorSummary} role="alert">
            {r.error}
          </p>
        )}
      </div>

      {result && state !== 'checking' && <FitAnswer result={result} scenario={asked} focusRef={resultRef} />}

      <Dialog
        open={talk}
        onClose={() => setTalk(false)}
        title={fitCheck.talk.title}
        actions={
          <button type="button" className={`${ui.button} ${ui.secondary}`} onClick={() => setTalk(false)}>
            {fitCheck.talk.keepChecking}
          </button>
        }
      >
        <p className={s.body}>{fitCheck.talk.body}</p>
        <BookMeeting />
      </Dialog>
    </div>
  );
}

function FitAnswer({ result, scenario, focusRef }: { result: FitResult; scenario: string; focusRef: React.RefObject<HTMLDivElement> }) {
  const r = fitCheck.result;
  const steps = NEXT_STEPS_BY_VERDICT[result.verdict].map((k) => fitCheck.nextSteps.items[k]);
  if (result.verdict === 'off_topic') return <OffTopicAnswer result={result} focusRef={focusRef} />;
  return (
    <div className={s.result} ref={focusRef} tabIndex={-1} aria-labelledby="fit-result-title">
      <div className={s.verdict}>
        <h2 id="fit-result-title" className="visually-hidden">
          {r.heading}
        </h2>
        <p className={s.small}>{r.verdictLabel}</p>
        <p className={s.verdictPill} data-tone={VERDICT_TONE[result.verdict]} data-verdict={result.verdict}>
          {verdictLabel(result.verdict)}
        </p>
        <p className={s.body}>{result.verdictText}</p>
      </div>

      {result.research && result.research.systems.length > 0 && <ResearchBlock research={result.research} />}

      <div className={s.resultGrid}>
        {(result.capabilities.length > 0 || result.systems.length > 0) && (
        <div className={s.resultBlock}>
          <h3>{r.capabilities}</h3>
          <ul>
            {result.capabilities.map((c) => (
              <li key={c.id}>
                <strong>{c.title}.</strong> {c.summary}
                {c.matchedOn && (
                  <span className={s.matched}>
                    {' '}
                    ({r.matchedOn} &ldquo;{c.matchedOn}&rdquo;)
                  </span>
                )}
              </li>
            ))}
            {result.systems.map((sys) => (
              <li key={sys.id}>
                <strong>{sys.name}.</strong> {sys.summary}
              </li>
            ))}
          </ul>
        </div>
        )}

        {(result.limits.length > 0 || result.volume || result.systems.some((sys) => sys.preferNativeWhen)) && (
        <div className={s.resultBlock}>
          <h3>{r.limits}</h3>
          <ul>
            {result.limits.map((l) => (
              <li key={l.id}>
                <strong>{l.title}.</strong> {l.summary}
              </li>
            ))}
            {result.systems
              .filter((sys) => sys.preferNativeWhen)
              .map((sys) => (
                <li key={`${sys.id}-native`}>{sys.preferNativeWhen}</li>
              ))}
            {result.volume && <li>{result.volume.note}</li>}
          </ul>
        </div>
        )}

        <div className={s.resultBlock}>
          <h3>{r.questions}</h3>
          <ol>
            {result.questions.map((q) => (
              <li key={q}>{q}</li>
            ))}
          </ol>
        </div>

        <div className={s.resultBlock}>
          <h3>{r.nextStep}</h3>
          <ul>
            {steps.map((item) => (
              <li key={item.lead}>
                <LeadText item={item} />
              </li>
            ))}
          </ul>
        </div>
      </div>

      {result.caveats.length > 0 && (
        <div className={s.resultBlock}>
          <h3>{r.caveats}</h3>
          <ul>
            {result.caveats.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
      )}

      {(result.verdict === 'needs_review' || result.verdict === 'partial') && <ReviewRequest scenario={scenario} />}
    </div>
  );
}

/** Only links to web pages: a source URL comes from a model reading the web. */
function safeUrl(u: string): string | null {
  try {
    const url = new URL(u);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
  } catch {
    return null;
  }
}

/** The systems the check looked up (ADR 0007 R1a): API and webhook support, a summary, and the vendor docs it read. */
function ResearchBlock({ research }: { research: NonNullable<FitResult['research']> }) {
  const r = fitCheck.result;
  return (
    <div className={s.resultBlock}>
      <h3>{r.research}</h3>
      <ul>
        {research.systems.map((sys) => {
          const links = sys.sources.map(safeUrl).filter((u): u is string => u !== null);
          return (
            <li key={sys.name}>
              <strong>{sys.name}:</strong> {r.api[sys.api]}. {r.webhooks[sys.webhooks]}. {sys.summary}
              {links.length > 0 && (
                <span className={s.matched}>
                  {' '}
                  ({r.sources}:{' '}
                  {links.map((u, i) => (
                    <span key={u}>
                      {i > 0 && ', '}
                      <a href={u} target="_blank" rel="noopener noreferrer nofollow">
                        {new URL(u).hostname}
                      </a>
                    </span>
                  ))}
                  )
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <p className={s.small}>
        {r.checkedOn} {new Date(research.checkedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
      </p>
    </div>
  );
}

/** A declined question: the verdict, what to describe instead, and an example. No capabilities, limits or caveats. */
function OffTopicAnswer({ result, focusRef }: { result: FitResult; focusRef: React.RefObject<HTMLDivElement> }) {
  const r = fitCheck.result;
  return (
    <div className={s.result} ref={focusRef} tabIndex={-1} aria-labelledby="fit-result-title">
      <div className={s.verdict}>
        <h2 id="fit-result-title" className="visually-hidden">
          {r.heading}
        </h2>
        <p className={s.small}>{r.verdictLabel}</p>
        <p className={s.verdictPill} data-tone={VERDICT_TONE.off_topic} data-verdict="off_topic">
          {verdictLabel('off_topic')}
        </p>
        <p className={s.body}>{result.verdictText}</p>
      </div>
      <div className={s.resultBlock}>
        <h3>{r.offTopicQuestions}</h3>
        <ul>
          {result.questions.map((q) => (
            <li key={q}>{q}</li>
          ))}
        </ul>
        <p className={s.body}>{result.nextStep}</p>
      </div>
    </div>
  );
}

interface ReviewReply {
  id: string;
  message: string;
}

/** The existing "ask a person" follow-up from the old /fit page: POST /v1/fit/review with the same question. */
export function ReviewRequest({ scenario }: { scenario: string }) {
  const c = fitCheck.review;
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<ReviewReply | null>(null);

  async function send(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const trimmed = email.trim();
      setSent(await publicPost<ReviewReply>('/v1/fit/review', { scenario, ...(trimmed ? { email: trimmed } : {}) }));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <p className={s.thanks} role="status">
        {c.sent}. {sent.message}
      </p>
    );
  }
  return (
    <form className={s.resultBlock} onSubmit={send} noValidate>
      <h3>{c.heading}</h3>
      <p className={s.small}>{c.body}</p>
      <Field label={c.emailLabel} error={error}>
        <Input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </Field>
      <div>
        <button type="submit" className={`${ui.button} ${ui.secondary}`} aria-busy={busy || undefined} disabled={busy}>
          {c.button}
        </button>
      </div>
    </form>
  );
}
